import { useWorkspaceInfoTeamsById } from '@modules/workspace/providers/WorkspaceInfo/hooks/useWorkspaceInfoTeamsById';
import { useCallback, useMemo } from 'react';
import { RolesRecord } from 'src/@types/roles';
import { WorkspaceParticipant } from 'src/@types/workspace-info';

export type FeatureWithRoleRecod<R extends string> = {
  roles: RolesRecord<R>;
  participants: WorkspaceParticipant[];
};

type Props<R extends string, F extends FeatureWithRoleRecod<R>> = {
  feature?: F;
  memberId: string;
};

export const useUserFeatureRole = <
  R extends string,
  F extends FeatureWithRoleRecod<R>,
>({
  feature,
  memberId,
}: Props<R, F>) => {
  const { teamsById } = useWorkspaceInfoTeamsById();

  const teamHasParticipant = useCallback(
    (teamId: string) =>
      teamsById[teamId]?.participants.some((p) => p === memberId),
    [memberId, teamsById],
  );

  const featureTeams = useMemo(() => {
    if (!feature?.participants?.length) return [];
    return feature?.participants?.reduce((acc: string[], participant) => {
      if ('team' in participant && teamHasParticipant(participant.team))
        return [...acc, participant.team];
      return acc;
    }, []);
  }, [feature, teamHasParticipant]);

  const role = useMemo((): R | null => {
    if (!feature?.roles) return null;
    let memberRole = null;

    Object.keys(feature.roles).every((r) => {
      if (
        feature.roles?.[r as R]?.[memberId] ||
        featureTeams.some((item) => feature.roles?.[r as R]?.[item])
      ) {
        memberRole = r;
        return false;
      }
      return true;
    });
    return memberRole;
  }, [feature, featureTeams, memberId]);

  const roles = useMemo((): Record<R, boolean> => {
    if (!feature?.roles) return {} as Record<R, boolean>;
    return Object.keys(feature.roles).reduce((acc, curr) => {
      if (
        feature.roles?.[curr as R]?.[memberId] ||
        featureTeams.some((item) => feature.roles?.[curr as R]?.[item])
      ) {
        return { ...acc, [curr]: true };
      }
      return acc;
    }, {}) as Record<R, boolean>;
  }, [feature, featureTeams, memberId]);
  return { role, roles };
};
